/**
 * PromptEditor — textarea-based MVP editor for a prompt version (Story 7.6).
 *
 * Controls:
 *   - `systemPrompt`   — large monospace textarea (min-h 24rem).
 *   - `fewShotsText`   — JSON textarea. Surfaces live parse errors so the
 *                        operator sees "x JSON inválido" below the field
 *                        without a full round-trip.
 *   - `jsonSchemaText` — same treatment as `fewShotsText`.
 *   - `changeSummary`  — optional one-line context, stored on the version.
 *
 * Decision (MVP): we use a plain `<textarea>` instead of `@monaco-editor/react`
 * so the admin bundle stays lean and the install graph doesn't blow up during
 * parallel Wave 5 dev. Swapping to Monaco is a drop-in later — just replace
 * this component while keeping the same props. Flagged as tech debt in the
 * story's Dev Agent Record.
 */

import * as React from 'react';

import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';

export interface PromptEditorValue {
  systemPrompt: string;
  fewShotsText: string;
  jsonSchemaText: string;
  changeSummary: string;
}

export interface PromptEditorErrors {
  fewShots?: string;
  jsonSchema?: string;
}

export interface PromptEditorProps {
  value: PromptEditorValue;
  onChange: (next: PromptEditorValue) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Parse the JSON-backed fields and surface per-field error strings. Returns
 * `null` for a field when the text is valid (or intentionally empty — empty
 * `fewShots` is allowed and becomes `[]`; empty `jsonSchema` becomes `{}`).
 */
export function validateEditor(value: PromptEditorValue): PromptEditorErrors {
  const errors: PromptEditorErrors = {};

  const fewShotsTrim = value.fewShotsText.trim();
  if (fewShotsTrim.length > 0) {
    try {
      const parsed = JSON.parse(fewShotsTrim);
      if (!Array.isArray(parsed)) {
        errors.fewShots = 'Deve ser um array JSON (ex: []).';
      }
    } catch (err) {
      errors.fewShots = `JSON inválido: ${(err as Error).message}`;
    }
  }

  const schemaTrim = value.jsonSchemaText.trim();
  if (schemaTrim.length > 0) {
    try {
      const parsed = JSON.parse(schemaTrim);
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        errors.jsonSchema = 'Deve ser um objeto JSON (ex: {}).';
      }
    } catch (err) {
      errors.jsonSchema = `JSON inválido: ${(err as Error).message}`;
    }
  }

  return errors;
}

export function PromptEditor({ value, onChange, disabled, className }: PromptEditorProps) {
  const errors = React.useMemo(() => validateEditor(value), [value]);

  const set = <K extends keyof PromptEditorValue>(key: K, v: PromptEditorValue[K]) => {
    onChange({ ...value, [key]: v });
  };

  return (
    <div className={cn('space-y-6', className)}>
      <div className="space-y-2">
        <div className="flex items-baseline justify-between">
          <Label htmlFor="editor-system-prompt">System prompt *</Label>
          <span className="text-xs text-muted-foreground">
            {value.systemPrompt.length} caracteres
          </span>
        </div>
        <textarea
          id="editor-system-prompt"
          value={value.systemPrompt}
          onChange={(e) => set('systemPrompt', e.target.value)}
          disabled={disabled}
          className="flex min-h-[24rem] w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs leading-relaxed ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          placeholder="# Identidade&#10;Você é…&#10;&#10;# Regra de Ouro&#10;…&#10;&#10;# Formato&#10;…"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="editor-fewshots">Few-shot examples (JSON array)</Label>
        <textarea
          id="editor-fewshots"
          value={value.fewShotsText}
          onChange={(e) => set('fewShotsText', e.target.value)}
          disabled={disabled}
          className="flex min-h-[10rem] w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs leading-relaxed ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          placeholder='[{"role": "user", "content": "..."}]'
        />
        {errors.fewShots && (
          <p className="text-xs text-destructive" role="alert">
            {errors.fewShots}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="editor-schema">JSON output schema</Label>
        <textarea
          id="editor-schema"
          value={value.jsonSchemaText}
          onChange={(e) => set('jsonSchemaText', e.target.value)}
          disabled={disabled}
          className="flex min-h-[10rem] w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs leading-relaxed ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          placeholder='{"type": "object", "properties": {...}}'
        />
        {errors.jsonSchema && (
          <p className="text-xs text-destructive" role="alert">
            {errors.jsonSchema}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="editor-change-summary">Resumo da mudança</Label>
        <textarea
          id="editor-change-summary"
          value={value.changeSummary}
          onChange={(e) => set('changeSummary', e.target.value)}
          disabled={disabled}
          className="flex min-h-[4rem] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          placeholder="O que mudou nesta versão? (opcional, ajuda o histórico)"
        />
      </div>
    </div>
  );
}

export default PromptEditor;
